import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, ArrowLeft, Users, Scale, Eye, Building2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import ThemeToggle from '../components/ThemeToggle';
import BrandLogo from '../components/BrandLogo';

const IndependencePage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      {/* Header */}
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md sticky top-0 z-40">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <BrandLogo size="responsive" data-testid="brand-logo-header" />
            <span className="text-lg sm:text-xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 dark:from-blue-400 dark:to-blue-600 bg-clip-text text-transparent">
              KaiCheckIt
            </span>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <ThemeToggle />
            <Button variant="outline" size="sm" onClick={() => navigate('/transparency')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="max-w-3xl mx-auto">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 rounded-lg bg-purple-100 dark:bg-purple-900/50">
                <Users className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
              <Badge className="bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300">
                Policy Document
              </Badge>
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-800 dark:text-slate-100 mb-4">
              Independence & Impartiality Policy
            </h1>
            <p className="text-slate-600 dark:text-slate-400">
              Last updated: February 2026
            </p>
          </div>

          {/* Content */}
          <div className="space-y-8">
            {/* Purpose */}
            <section>
              <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                Purpose
              </h2>
              <Card className="dark:bg-slate-800 dark:border-slate-700">
                <CardContent className="pt-6">
                  <p className="text-slate-700 dark:text-slate-300">
                    KaiCheckIt operates independently of political, ideological, commercial, or governmental influence.
                  </p>
                </CardContent>
              </Card>
            </section>

            {/* Editorial Independence */}
            <section>
              <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-4 flex items-center gap-2">
                <Scale className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                Editorial Independence
              </h2>
              <Card className="dark:bg-slate-800 dark:border-slate-700">
                <CardContent className="pt-6 space-y-4">
                  <p className="text-slate-700 dark:text-slate-300">Verification outputs are produced without:</p>
                  <ul className="list-disc list-inside text-slate-700 dark:text-slate-300 space-y-1">
                    <li>Direction from funders</li>
                    <li>Influence from advertisers</li>
                    <li>Pressure from political or advocacy groups</li>
                  </ul>
                  <p className="text-slate-700 dark:text-slate-300 font-medium border-l-4 border-purple-500 pl-4 bg-purple-50 dark:bg-purple-900/20 py-2">
                    KaiCheckIt does not support political parties, candidates, or policy agendas.
                  </p>
                </CardContent>
              </Card>
            </section>

            {/* Impartiality */}
            <section>
              <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-4 flex items-center gap-2">
                <Eye className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                Impartiality
              </h2>
              <Card className="dark:bg-slate-800 dark:border-slate-700">
                <CardContent className="pt-6 space-y-4">
                  <p className="text-slate-700 dark:text-slate-300">KaiCheckIt:</p>
                  <ul className="list-disc list-inside text-slate-700 dark:text-slate-300 space-y-1">
                    <li>Applies the same verification standards to all claims</li>
                    <li>Does not target or favor specific groups or viewpoints</li>
                    <li>Focuses on claims, not people</li>
                  </ul>
                </CardContent>
              </Card>
            </section>

            {/* Governance */}
            <section>
              <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-4 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                Governance
              </h2>
              <Card className="dark:bg-slate-800 dark:border-slate-700">
                <CardContent className="pt-6">
                  <p className="text-slate-700 dark:text-slate-300">
                    Operational decisions, verification standards, and corrections are governed internally and are not delegated to external stakeholders.
                  </p>
                </CardContent>
              </Card>
            </section>

            {/* Accountability */}
            <section>
              <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                Accountability
              </h2>
              <Card className="dark:bg-slate-800 dark:border-slate-700">
                <CardContent className="pt-6">
                  <p className="text-slate-700 dark:text-slate-300">
                    KaiCheckIt welcomes scrutiny, feedback, and correction as part of responsible verification practice.
                  </p>
                </CardContent>
              </Card>
            </section>
          </div>

          {/* Back Link */}
          <div className="mt-10 text-center">
            <Button variant="outline" onClick={() => navigate('/transparency')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Transparency & Accountability
            </Button>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 dark:border-slate-700 bg-white/50 dark:bg-slate-900/50 py-6 mt-12">
        <div className="container mx-auto px-4 text-center text-sm text-slate-500 dark:text-slate-400">
          © 2025 Kaiventive Holdings. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default IndependencePage;
